import { productDescriptor } from '../descriptors/product.descriptor.js';
import { resolveProductStockControl } from '../services/productStockControl.js';
import { MAX_CODE_LARGE, MAX_QUANTITY, MAX_QUANTITY_DECIMALS } from '../validation/fieldLimits.js';
import { mergeErrors } from './mergeErrors.js';
import { integer, max, nonNegative } from './rules.js';
import { runDescriptorValidation } from './runDescriptorValidation.js';
import { validateWithRules } from './validateWithRules.js';
const QUANTITY_FIELDS = [
    'salePrice',
    'purchasePrice',
    'unitWeight',
    'grossWeight',
    'grammage',
    'entryWidth',
    'exitWidth',
];
const QUANTITY_RULES = Object.fromEntries(QUANTITY_FIELDS.map((field) => [
    field,
    [
        nonNegative('Não pode ser negativo.'),
        max(MAX_QUANTITY, 'Valor acima do máximo permitido (999.999.999.999,999).'),
    ],
]));
/**
 * L0 + L1 validation for Product. Single authority for console onValidate,
 * ProductService.create/update, and future mobile/direct writers.
 *
 * Stock-control cascade: pass org stock params via `ctx.orgParams.stock`
 * (same shape as `useOrgParams('stock').values`).
 */
export function validateProduct(entity, ctx) {
    // fase D: block changing stock-control flags when the product has balance or
    // movements — prevents orphan balances on a key that stopped being used.
    const acceptedSizes = entity.acceptedSizeOccurrenceIds;
    const hasAcceptedSizes = Array.isArray(acceptedSizes) && acceptedSizes.length > 0;
    const sizeGridError = hasAcceptedSizes && !entity.sizeGridId
        ? { sizeGridId: 'Selecione a grade antes de escolher os tamanhos aceitos.' }
        : undefined;
    return mergeErrors(runDescriptorValidation(productDescriptor, entity, [
        'code',
        'reference',
        'barcode',
        'ncm',
        'description',
        'unit',
    ]), validateWithRules(entity, {
        code: [
            integer('Informe um código inteiro não negativo.'),
            nonNegative('Informe um código inteiro não negativo.'),
            max(MAX_CODE_LARGE, 'Valor acima do máximo permitido (999.999.999).'),
        ],
        quantityDecimals: [
            integer('Informe um número inteiro de casas decimais.'),
            nonNegative('Não pode ser negativo.'),
            max(MAX_QUANTITY_DECIMALS, 'Máximo de 4 casas decimais.'),
        ],
        ...QUANTITY_RULES,
    }, ctx), sizeGridError, validateStockControlCascade(entity, ctx));
}
function validateStockControlCascade(entity, ctx) {
    const orgStock = ctx?.orgParams?.stock ??
        ctx?.extra?.['stockOrgParams'] ??
        null;
    const resolved = resolveProductStockControl(entity, orgStock);
    const errors = {};
    const check = (key, label) => {
        const raw = entity[key] === true;
        const effective = resolved.values[key];
        if (raw && !effective) {
            errors[key] = resolved.reasons[key] ?? label;
        }
    };
    // When org params are available, reject raw flags that the cascade would force off.
    // When absent (direct writers without ctx), still enforce internal expiry→lot.
    if (orgStock) {
        check('stockControlEnabled', 'Controle de estoque desativado na organização.');
        check('stockControlByLot', 'Controle de lote não permitido nesta combinação.');
        check('stockHasExpiry', 'Validade não permitida nesta combinação.');
        check('stockControlByVolume', 'Controle por volume requer estoque ativo.');
    }
    else if (entity.stockHasExpiry === true && entity.stockControlByLot !== true) {
        errors.stockHasExpiry = 'Requer controle de lote';
    }
    // Internal: expiry without lot (also when org params present and raw contradicts)
    if (entity.stockHasExpiry === true && resolved.values.stockControlByLot !== true) {
        errors.stockHasExpiry = resolved.reasons.stockHasExpiry ?? 'Requer controle de lote';
    }
    return Object.keys(errors).length > 0 ? errors : undefined;
}
//# sourceMappingURL=validateProduct.js.map